/**
 * Decision-product registry.
 *
 * This is the product-level contract used by Path to GO and Findings. The
 * existing report-type registry still owns labels, prompts, and legacy report
 * metadata; this registry owns lifecycle behavior: prerequisite authority,
 * semantic runner, HTML reader, and eligible export formats.
 */
import type { ReportTypeId } from './reportTypes';

export type DecisionProductPrerequisiteKind =
  | 'sed-preflight'
  | 'test-fit-authority'
  | 'financial-model'
  | 'equipment-bom'
  | 'custom-brief-inputs'
  /**
   * The declared facility load. Power Availability's spine is firm-capacity
   * (ELCC) adequacy measured AGAINST that load — without it the assessment has
   * no denominator, and its grid-vs-BTM and LCOE sections assert a technology
   * and cost conclusion with nothing to size them against.
   */
  | 'declared-load'
  | 'live-rollup';

export type DecisionProductSemanticRunnerKind =
  | 'decision-analysis'
  | 'custom-brief'
  | 'live-rollup';

export type DecisionProductHtmlRendererKind =
  | 'analysis-reader'
  | 'report-reader'
  | 'decision-guide';

export interface DecisionProductDefinition {
  id: ReportTypeId;
  generation: 'generated' | 'live' | 'retired';
  prerequisite: DecisionProductPrerequisiteKind;
  semanticRunner: DecisionProductSemanticRunnerKind;
  htmlRenderer: DecisionProductHtmlRendererKind;
  exports: {
    pdf: true;
    docx: true;
    pptx: true;
  };
}

const GENERATED_EXPORTS = {
  pdf: true,
  docx: true,
  pptx: true,
} as const;

/**
 * Stable Analyze ordering. Path to GO dynamically moves required products to
 * the front while preserving this order for the optional catalog below them.
 */
export const GENERATED_DECISION_PRODUCT_IDS = [
  'feasibility',
  'power-availability',
  'test-fit',
  'environmental',
  'interconnection',
  'financial',
  'supply-chain-risk',
  'public-sentiment',
  'custom-brief',
] as const satisfies readonly ReportTypeId[];

export const LIVE_DECISION_PRODUCT_IDS = [
  'executive-summary',
] as const satisfies readonly ReportTypeId[];

/** Historical analyses retain their readers/export contracts, without a run card. */
export const RETIRED_DECISION_PRODUCT_IDS = ['water-resource'] as const satisfies readonly ReportTypeId[];

export const DECISION_PRODUCT_REGISTRY: Record<
  ReportTypeId,
  DecisionProductDefinition
> = {
  feasibility: {
    id: 'feasibility',
    generation: 'generated',
    prerequisite: 'sed-preflight',
    semanticRunner: 'decision-analysis',
    htmlRenderer: 'analysis-reader',
    exports: GENERATED_EXPORTS,
  },
  environmental: {
    id: 'environmental',
    generation: 'generated',
    prerequisite: 'sed-preflight',
    semanticRunner: 'decision-analysis',
    htmlRenderer: 'analysis-reader',
    exports: GENERATED_EXPORTS,
  },
  'test-fit': {
    id: 'test-fit',
    generation: 'generated',
    prerequisite: 'test-fit-authority',
    semanticRunner: 'decision-analysis',
    htmlRenderer: 'analysis-reader',
    exports: GENERATED_EXPORTS,
  },
  'power-availability': {
    id: 'power-availability',
    generation: 'generated',
    prerequisite: 'declared-load',
    semanticRunner: 'decision-analysis',
    htmlRenderer: 'analysis-reader',
    exports: GENERATED_EXPORTS,
  },
  'water-resource': {
    id: 'water-resource',
    generation: 'retired',
    prerequisite: 'sed-preflight',
    semanticRunner: 'decision-analysis',
    htmlRenderer: 'analysis-reader',
    exports: GENERATED_EXPORTS,
  },
  interconnection: {
    id: 'interconnection',
    generation: 'generated',
    prerequisite: 'sed-preflight',
    semanticRunner: 'decision-analysis',
    htmlRenderer: 'analysis-reader',
    exports: GENERATED_EXPORTS,
  },
  'supply-chain-risk': {
    id: 'supply-chain-risk',
    generation: 'generated',
    prerequisite: 'equipment-bom',
    semanticRunner: 'decision-analysis',
    htmlRenderer: 'analysis-reader',
    exports: GENERATED_EXPORTS,
  },
  financial: {
    id: 'financial',
    generation: 'generated',
    prerequisite: 'financial-model',
    semanticRunner: 'decision-analysis',
    htmlRenderer: 'analysis-reader',
    exports: GENERATED_EXPORTS,
  },
  'public-sentiment': {
    id: 'public-sentiment',
    generation: 'generated',
    prerequisite: 'sed-preflight',
    semanticRunner: 'decision-analysis',
    htmlRenderer: 'analysis-reader',
    exports: GENERATED_EXPORTS,
  },
  'custom-brief': {
    id: 'custom-brief',
    generation: 'generated',
    prerequisite: 'custom-brief-inputs',
    semanticRunner: 'custom-brief',
    htmlRenderer: 'report-reader',
    exports: GENERATED_EXPORTS,
  },
  'executive-summary': {
    id: 'executive-summary',
    generation: 'live',
    prerequisite: 'live-rollup',
    semanticRunner: 'live-rollup',
    htmlRenderer: 'decision-guide',
    exports: GENERATED_EXPORTS,
  },
};

export function getDecisionProduct(
  id: ReportTypeId,
): DecisionProductDefinition {
  return DECISION_PRODUCT_REGISTRY[id];
}
